import React, { useCallback, useContext, useEffect, useState } from 'react'
import { useSnackbar } from 'notistack'
import {
  Box,
  Card,
  CardContent,
  Grid,
  TextField,
  Typography
} from '@mui/material'
import { FormProvider, useForm } from 'react-hook-form'
import loginService from '../../../services/loginService'
import { AuthTokenContext } from '../../contexts/AuthTokenContext'
import { UserInfoContext } from '../../contexts/UserInfoContext'
import { Link, useNavigate } from 'react-router-dom'
import SubmitButton from '../../commonUIComponents/SubmitButton'
import {
  LoginSocialGoogle,
  LoginSocialFacebook,
  LoginSocialMicrosoft
} from 'reactjs-social-login'
import {
  FacebookLoginButton,
  GoogleLoginButton,
  MicrosoftLoginButton
} from 'react-social-login-buttons'
import resetPasswordService from '../../../services/resetPasswordService'
import ResetPasswordModal from './ResetPasswordModal'

const LoginPage = () => {
  const { setAuthToken } = useContext(AuthTokenContext)
  const { setUserInfo } = useContext(UserInfoContext)
  const [openResetPasswordModal, setOpenResetPasswordModal] = useState(false)
  const { enqueueSnackbar } = useSnackbar()
  const navigate = useNavigate()
  const methods = useForm({ mode: 'onChange' })
  const {
    register,
    handleSubmit,
    formState: { errors }
  } = methods
  const [provider, setProvider] = useState(null)
  const [profile, setProfile] = useState(null)

  useEffect(() => {
    if (provider && profile) {
      let form
      if (provider === 'google') {
        form = { authProvider: provider, authToken: profile.access_token }
      } else if (provider === 'facebook') {
        form = { authProvider: provider, authToken: profile.accessToken }
      } else if (provider === 'microsoft') {
        form = { authProvider: provider, authToken: profile.access_token, id: profile.id }
      }
      loginService.socialLogin(form)
        .then(res => {
          setAuthTokenAndUser(res.token, res.user)
          enqueueSnackbar(`Welcome! ${res.user.nickname}.`)
          navigate('/home')
        })
        .catch(err => enqueueSnackbar(err.response?.data.message, { variant: 'warning' }))
    }
  }, [provider, profile])

  const onLoginStart = useCallback(() => {
    // console.log('login start')
  }, [])

  const submitHandler = values => {
    return loginService.login(values)
      .then(res => {
        setAuthTokenAndUser(res.token, res.user)
        enqueueSnackbar(`Welcome! ${res.user.nickname}.`)
        navigate('/home')
      })
      .catch(err => enqueueSnackbar(err.response?.data.message, { variant: 'warning' }))
  }

  const setAuthTokenAndUser = (token, user) => {
    loginService.setToken(token)
    setAuthToken(token)
    loginService.setUser(JSON.stringify(user))
    setUserInfo(JSON.stringify(user))
  }

  const resetPasswordModalCloseHandler = () => {
    setOpenResetPasswordModal(false)
  }

  const resetPasswordOnClickHandler = (event) => {
    event.preventDefault()
    setOpenResetPasswordModal(true)
  }

  const resetPasswordOnSubmitHandler = values => {
    resetPasswordModalCloseHandler()
    enqueueSnackbar('An email with a link to reset your password was sent to the email address associated with your account', { variant: 'success' })
    const formValues = { username: values.resetPasswordUsername }
    return resetPasswordService.sendResetPasswordLink(formValues).then(res => console.log(res))
  }

  return (
    <>
      <Box>
         {openResetPasswordModal && <ResetPasswordModal methods={methods} open={openResetPasswordModal} onClose={resetPasswordModalCloseHandler} submitOnSuccess={resetPasswordOnSubmitHandler} />}
        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(submitHandler)}>
            <Grid container spacing={2}>
              <Grid item xs={4}/>
              <Grid item xs={4}>
                <Card variant="outlined">
                  <CardContent>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <Typography variant="h5" component="div">
                          Login
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="body2">
                          By continuing, you agree to our User Agreement and acknowledge that you understand the Privacy Policy.
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sx={{ alignContent: 'center' }}>
                         <LoginSocialGoogle
                          isOnlyGetToken
                          scope="openid profile email"
                          client_id={process.env.REACT_APP_GG_APP_ID || ''}
                          onLoginStart={onLoginStart}
                          onResolve={({ provider, data }) => {
                            setProvider(provider)
                            setProfile(data)
                          }}
                          onReject={(err) => {
                            console.log(err)
                          }}
                         >
                          <GoogleLoginButton style={{ borderRadius: 15 }} />
                         </LoginSocialGoogle>
                      </Grid>
                      <Grid item xs={12} sx={{ alignContent: 'center' }}>
                        <LoginSocialFacebook
                          isOnlyGetToken
                          appId={process.env.REACT_APP_FB_APP_ID || ''}
                          onLoginStart={onLoginStart}
                          onResolve={({ provider, data }) => {
                            setProvider(provider)
                            setProfile(data)
                          }}
                          onReject={(err) => {
                            console.log(err)
                          }}
                        >
                          <FacebookLoginButton style={{ borderRadius: 15 }} />
                        </LoginSocialFacebook>
                      </Grid>
                      <Grid item xs={12} sx={{ alignContent: 'center' }}>
                        <LoginSocialMicrosoft
                          // isOnlyGetToken
                          scope="User.Read"
                          client_id={process.env.REACT_APP_MICROSOFT_APP_ID || ''}
                          redirect_uri="http://localhost:3000/login"
                          onLoginStart={onLoginStart}
                          onResolve={({ provider, data }) => {
                            setProvider(provider)
                            setProfile(data)
                          }}
                          onReject={(err) => {
                            console.log(err)
                          }}
                        >
                          <MicrosoftLoginButton style={{ borderRadius: 15 }} />
                        </LoginSocialMicrosoft>
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          label="Username"
                          {...register('username', { required: { value: true, message: 'Invalid Username' } })}
                          error={errors.username}
                          helperText={errors.username?.message}
                          fullWidth
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          label="Password"
                          type="password"
                          {...register('password', { required: { value: true, message: 'Invalid Password' } })}
                          error={errors.password}
                          helperText={errors.password?.message}
                          fullWidth
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="subtitle2" gutterBottom component="div">
                          <Link to="#" onClick={resetPasswordOnClickHandler}>Forgot password?</Link>
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="subtitle2" gutterBottom component="div">
                          New to Redditlike? <Link to="/signup" component={Link}>Sign Up</Link>
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <SubmitButton buttonLabel="Log In" loadingLabel="Logging in..." />
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={4}/>
            </Grid>
          </form>
        </FormProvider>
      </Box>
    </>
  )
}

export default LoginPage
